import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Search, Plus, Eye, RefreshCw, FileQuestion, CheckCircle2, Clock, XCircle, ArrowRight, Edit } from 'lucide-react';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusBadge = (status) => {
  const badges = {
    'OPEN': { color: 'bg-orange-100 text-orange-800', icon: Clock },
    'IN_PROCESS': { color: 'bg-yellow-100 text-yellow-800', icon: ArrowRight },
    'CONVERTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
  };
  const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileQuestion };
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const InquiryList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [filters, setFilters] = useState({ customer_id: '', status: '', sales_org: '' });

  useEffect(() => {
    loadMasterData();
    loadInquiries();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const [custRes, orgRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadInquiries = async () => {
    setIsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/presales/inquiries`;
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.sales_org) params.append('sales_org', filters.sales_org);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setInquiries(response.data || []);
    } catch (error) {
      console.error('Error loading inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadInquiries();

  const columns = [
    { key: 'inquiry_number', header: 'Inquiry #', filterable: true, accessor: (inq) => inq.inquiry_number,
      render: (inq) => (
        <button onClick={() => navigate(`/presales/inquiries/${inq.inquiry_number}`)} className="font-mono font-medium text-orange-600 hover:underline">
          {inq.inquiry_number}
        </button>
      ) },
    { key: 'customer', header: 'Customer', filterable: true, accessor: (inq) => `${inq.customer_id} ${inq.customer_name}`,
      render: (inq) => (
        <div>
          <p className="font-medium text-gray-900">{inq.customer_id}</p>
          <p className="text-sm text-gray-500">{inq.customer_name}</p>
        </div>
      ) },
    { key: 'sales_organization', header: 'Sales Org', accessor: (inq) => inq.sales_organization },
    { key: 'inquiry_date', header: 'Inquiry Date', accessor: (inq) => inq.inquiry_date },
    { key: 'items', header: 'Items', align: 'center',
      render: (inq) => <span className="px-2 py-1 bg-gray-100 rounded text-sm">{inq.items?.length || 0}</span> },
    { key: 'status', header: 'Status', align: 'center', render: (inq) => getStatusBadge(inq.status) },
    { key: 'quotations', header: 'Quotations',
      render: (inq) => (inq.quotation_numbers?.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {inq.quotation_numbers.map(qt => (
            <button key={qt} onClick={() => navigate(`/presales/quotations/${qt}`)} className="font-mono text-xs text-green-600 hover:underline">{qt}</button>
          ))}
        </div>
      ) : <span className="text-gray-400">-</span>) },
    { key: 'actions', header: 'Actions', align: 'center',
      render: (inq) => (
        <div className="flex justify-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/presales/inquiries/${inq.inquiry_number}`)} data-testid={`view-inquiry-${inq.inquiry_number}`}>
            <Eye className="w-4 h-4" />
          </Button>
          {canWrite('inquiry') && (inq.status === 'OPEN' || inq.status === 'IN_PROCESS') && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/presales/inquiries/${inq.inquiry_number}/edit`)} data-testid={`edit-inquiry-${inq.inquiry_number}`}>
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6" data-testid="inquiry-list">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Inquiries</h1>
            <p className="text-sm text-gray-600">Manage customer interest and requests</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadInquiries} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canWrite('inquiry') && (
            <Button onClick={() => navigate('/presales/inquiries/create')} data-testid="create-inquiry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Inquiry
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_id} - {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROCESS">In Process</SelectItem>
                  <SelectItem value="CONVERTED">Converted</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Org</label>
              <Select value={filters.sales_org || '__ALL__'} onValueChange={(v) => setFilters({...filters, sales_org: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Sales Orgs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Sales Orgs</SelectItem>
                  {salesOrgs.map(org => (
                    <SelectItem key={org.sales_org_id} value={org.sales_org_id}>{org.sales_org_id} - {org.sales_org_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-6">
          <DataTable
            testid="inquiry-table"
            columns={columns}
            data={inquiries}
            loading={isLoading}
            rowKey={(inq) => inq.inquiry_number}
            emptyText="No inquiries found"
            emptyHint="Create an inquiry to get started."
          />
        </Card>
      </div>
    </Layout>
  );
};

export default InquiryList;
